import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { PLANS } from 'constant/plan';
import { useCreditSummary } from 'features/credits/hooks/use-credits';
import { usageEnforcementService } from 'features/usage-config/services/usage-enforcement.service';
import { useGlobalMutation, useGlobalQuery } from 'state/query-client/hooks';
import { useAuthStore } from 'state/store/auth/useAuthStore';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { visibleSubscriptionPlans } from '../constants/plan-display';
import {
  buySeats,
  cancelPlan,
  getMyPlaces,
  getSeatPools,
  getSubscriptionInvoices,
  keepPlan,
  moveOntoSeats,
  quoteSeats,
  releaseSeat,
  requestSeatSync,
  SeatPool,
} from '../services/seat-service';
import { queuePendingSeats } from '../utils/pending-seats';
import {
  buildPlanSeats,
  PlanSeats,
  quantityForMore,
  SUBSCRIPTION_NOT_LIVE,
  utilitiesErrorCode,
  utilitiesErrorMessage,
} from '../utils/seat-pools';
import { useGetSubscriptionPlans } from './use-subscription';

export const SEAT_POOLS_QUERY_KEY = 'seat-pools';

export interface SeatMover {
  userId: string;
  /** The plan the person is on now, whose seat is released once the new one is theirs. */
  from?: PlanSeats;
}

export interface MoveSeatsRequest {
  people: SeatMover[];
  /** The plan to put them on. None means no plan. */
  to?: PlanSeats;
  /** How far to raise the seat count on `to` first, because too few can be filled. */
  add?: number;
}

export interface SeatChange {
  entry: PlanSeats;
  quantity: number;
}

/** A first purchase of a plan may need the payment page. Opened last, once the seats are given. */
const goToPayment = (url: string | null | undefined) => {
  if (url) window.location.href = url;
};

/**
 * The organization's plans and who holds their seats. `fresh` reads them again on every mount,
 * for a screen whose figures must not come from a copy cached before the latest charges.
 */
export const useSeatPools = (enabled = true, fresh = false) => {
  const { configurationSettings } = useConfig();
  const selectedOrgId = useAuthStore((s) => s.selectedOrgId);
  const user = useCommonStore((s) => s.user);
  const organizationId = selectedOrgId || user?.organizationId || '';

  return useGlobalQuery({
    queryKey: [SEAT_POOLS_QUERY_KEY, organizationId],
    queryFn: () => getSeatPools(configurationSettings),
    enabled: enabled && !!configurationSettings,
    ...(fresh && { staleTime: 0, refetchOnMount: 'always' as const }),
  });
};

/** The organization's invoices from Utilities (seats and credits), with their status (paid, voided and so on). */
export const useSubscriptionInvoices = () => {
  const { configurationSettings } = useConfig();
  const selectedOrgId = useAuthStore((s) => s.selectedOrgId);
  const user = useCommonStore((s) => s.user);

  return useGlobalQuery({
    queryKey: [SEAT_POOLS_QUERY_KEY, 'invoices', selectedOrgId || user?.organizationId || ''],
    queryFn: () => getSubscriptionInvoices(configurationSettings),
    enabled: !!configurationSettings,
    refetchOnMount: 'always',
  });
};

/**
 * An organization whose usage enforcement is switched off uses the site for free: no seat or
 * subscription is checked for its people.
 */
export const useFreeAccess = () => {
  const { configurationSettings } = useConfig();
  const selectedOrgId = useAuthStore((s) => s.selectedOrgId);
  const user = useCommonStore((s) => s.user);
  const organizationId = selectedOrgId || user?.organizationId || '';

  const { data, isLoading } = useGlobalQuery({
    queryKey: ['free-access', organizationId],
    queryFn: () => usageEnforcementService.getOrganizationEnforcement(organizationId),
    enabled: !!configurationSettings && !!organizationId,
    retry: false,
    // Only an admin changes this, so one check per login (logout clears the cache) or org switch is enough.
    staleTime: Infinity,
  });
  return { isFreeAccess: data?.enforcement_enabled === false, isLoading };
};

/**
 * The plan the signed-in person is on: the first seat they hold, or none. `hasPlan` is also true
 * for an organization with free access, which skips the seat check, and for one whose top-up
 * credits are not used up, since those pay for chat, agents and the knowledge base too. A member
 * switched off from the top-up credits does not get the second.
 */
export const useMyPlan = () => {
  const { configurationSettings } = useConfig();
  const selectedOrgId = useAuthStore((s) => s.selectedOrgId);
  const user = useCommonStore((s) => s.user);
  const { isFreeAccess, isLoading: isFreeAccessLoading } = useFreeAccess();

  const { data: places = [], isLoading } = useGlobalQuery({
    queryKey: [SEAT_POOLS_QUERY_KEY, 'mine', selectedOrgId || user?.organizationId || '', user?.id],
    queryFn: () => getMyPlaces(configurationSettings),
    // Fetched even with free access: the seat is still shown in the profile menu and settings.
    enabled: !!configurationSettings && !!user?.id,
  });
  const { data: credits, isLoading: isCreditsLoading } = useCreditSummary(false);
  const hasTopUpCredits = !!credits?.enabled && credits.hasAccess !== false && credits.balanceCredits > 0;
  const place = places[0];
  const hasPlan = isFreeAccess || hasTopUpCredits || (!!place && place.planCode !== PLANS.NOPLAN);
  return { place, hasPlan, isFreeAccess, isLoading: isFreeAccessLoading || isLoading || isCreditsLoading };
};

// Each expired checkout is cancelled once, however many screens read the plans at the same time.
const cancelledExpiredCheckouts = new Set<string>();

/**
 * Every plan with its seats, bought or not. A plan waiting for its first payment whose payment page
 * has expired can only be cancelled, so it is cancelled here and left out, as if already gone.
 */
export const usePlanSeats = (fresh = false) => {
  const { data: plans, isLoading: isLoadingPlans } = useGetSubscriptionPlans();
  const { data: pools = [], isLoading: isLoadingPools } = useSeatPools(true, fresh);
  const { mutate: cancel } = useCancelPlan();
  const expired = pools.filter((pool) => pool.subscription.status === 'Incomplete' && !pool.subscription.checkoutUrl);
  const livePools = expired.length ? pools.filter((pool) => !expired.includes(pool)) : pools;

  useEffect(() => {
    for (const pool of expired) {
      const subscriptionId = pool.subscription.subscriptionId;
      if (cancelledExpiredCheckouts.has(subscriptionId)) continue;
      cancelledExpiredCheckouts.add(subscriptionId);
      cancel({ pool, now: true });
    }
  }, [expired, cancel]);

  return {
    planSeats: buildPlanSeats(visibleSubscriptionPlans(plans?.data ?? []), livePools),
    isLoading: isLoadingPlans || isLoadingPools,
  };
};

/** What setting this plan to `quantity` seats costs, or null when nothing would change. */
export const useSeatQuote = (entry: PlanSeats | null | undefined, quantity: number | null) => {
  const { configurationSettings } = useConfig();
  const changes = !!entry && quantity != null && quantity !== entry.seatsNextPeriod && quantity > 0;

  return useGlobalQuery({
    queryKey: [SEAT_POOLS_QUERY_KEY, 'quote', entry?.code, entry?.price?.priceId, entry?.seatsNextPeriod, quantity],
    queryFn: () => quoteSeats(entry as PlanSeats, quantity as number, configurationSettings),
    enabled: changes,
    retry: false,
  });
};

const useSeatMutation = <TVariables, TData = void>(key: string, run: (variables: TVariables) => Promise<TData>) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationKey: [key],
    mutationFn: run,
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [SEAT_POOLS_QUERY_KEY] });
    },
    onError: (error: unknown) => {
      toast({
        variant: 'destructive',
        title: t('ERROR'),
        description: utilitiesErrorMessage(error) || t('SEAT_CHANGE_FAILED', 'The seats could not be changed.'),
      });
    },
  });
};

/**
 * Moves one person or many onto a plan, buying the missing seats first, in as few calls as Utilities
 * allows. The caller has already checked the plan can take them (see `seatMoveProblem`), so a
 * purchase is only made for a move that can land. A subscription waiting for its first payment
 * cannot seat anyone, so the move is remembered and made once the payment lands. Resolves true only
 * when the people are seated now, and false when the move waits for a payment or nobody is seated.
 */
export const useMoveSeats = () => {
  const { configurationSettings } = useConfig();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useSeatMutation('move-seats', async ({ people, to, add = 0 }: MoveSeatsRequest): Promise<boolean> => {
    let subscriptionId = to?.pool?.subscription.subscriptionId;
    let paymentUrl: string | null = null;
    const isBuying = !!to && (add > 0 || !subscriptionId);
    if (to && isBuying) {
      const purchase = await buySeats(to, quantityForMore(to, Math.max(add, 1)), configurationSettings);
      subscriptionId = purchase.subscriptionId;
      paymentUrl = purchase.checkoutUrl;
    }
    const releases = people.flatMap(({ userId, from }) => {
      const fromId = from?.pool?.subscription.subscriptionId;
      return fromId && fromId !== subscriptionId ? [{ subscriptionId: fromId, userId }] : [];
    });
    if (!subscriptionId) {
      for (const release of releases) {
        await releaseSeat(release.subscriptionId, release.userId, configurationSettings);
      }
      return false;
    }
    const userIds = people.map((person) => person.userId);
    const wait = () => queuePendingSeats({ subscriptionId: subscriptionId as string, userIds, releases });
    if (paymentUrl) {
      wait();
      goToPayment(paymentUrl);
      return false;
    }
    try {
      await moveOntoSeats(subscriptionId, userIds, releases, configurationSettings);
      requestSeatSync(userIds, configurationSettings);
      // Charged to the saved card with no payment page, so the seat is paid and given now.
      if (isBuying) {
        toast({ variant: 'success', title: t('SUCCESS'), description: t('SEAT_BOUGHT_AND_ASSIGNED', 'The seat is paid and assigned.') });
      }
      return true;
    } catch (error) {
      if (utilitiesErrorCode(error) !== SUBSCRIPTION_NOT_LIVE) throw error;
      wait();
      return false;
    }
  });
};

export const useCancelPlan = () => {
  const { configurationSettings } = useConfig();
  return useSeatMutation('cancel-plan', ({ pool, now }: { pool: SeatPool; now: boolean }) =>
    cancelPlan(pool, now, configurationSettings)
  );
};

export const useKeepPlan = () => {
  const { configurationSettings } = useConfig();
  return useSeatMutation('keep-plan', (subscriptionId: string) => keepPlan(subscriptionId, configurationSettings));
};

/** Applies each plan's new seat count, one plan at a time. */
export const useBuySeats = () => {
  const { configurationSettings } = useConfig();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useSeatMutation('buy-seats', async (changes: SeatChange[]) => {
    let paymentUrl: string | null = null;
    for (const { entry, quantity } of changes) {
      paymentUrl = (await buySeats(entry, quantity, configurationSettings)).checkoutUrl ?? paymentUrl;
    }
    // Nothing is bought until the payment page is paid, so only a direct charge gets the toast.
    if (paymentUrl) return goToPayment(paymentUrl);
    if (changes.some(({ entry, quantity }) => quantity > entry.seatsNextPeriod)) {
      toast({ variant: 'success', title: t('SUCCESS'), description: t('SEATS_BOUGHT', 'Your new seats are ready to assign.') });
    }
  });
};
